import {
  S3Client,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  ListPartsCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import type { DashboardUploaderS3Cfg } from '@/lib/types/Upload';

export class S3MultipartSigner {
  private static cachedClient: S3Client | null = null;
  private static cachedCacheKey: string = '';

  private static getClient(cfg: DashboardUploaderS3Cfg): S3Client {
    const cacheKey = `${cfg.accessKey}:${cfg.expirationTS}`;
    if (this.cachedClient && this.cachedCacheKey === cacheKey) {
      return this.cachedClient;
    }
    this.cachedClient = new S3Client({
      region: cfg.region,
      credentials: {
        accessKeyId: cfg.accessKey,
        secretAccessKey: cfg.secretKey,
        sessionToken: cfg.sessionToken,
      },
      useAccelerateEndpoint: true,
      // Backend reports drift in seconds; SDK expects ms.
      // Prevents 403 signature errors when client clock is off by >15 min.
      systemClockOffset: cfg.clockDriftSecs * 1000,
      // Disable automatic CRC32 checksums added by AWS SDK v3.
      // Without this, the presigner adds x-amz-checksum-crc32 as a signed header
      // and x-amz-sdk-checksum-algorithm=CRC32 as a query param. The browser's
      // PUT request doesn't include the checksum header, causing SignatureDoesNotMatch.
      requestChecksumCalculation: 'WHEN_REQUIRED',
      responseChecksumValidation: 'WHEN_REQUIRED',
    });
    this.cachedCacheKey = cacheKey;
    return this.cachedClient;
  }

  static async createMultipartUpload(
    cfg: DashboardUploaderS3Cfg,
    key: string,
    contentType: string
  ): Promise<{ uploadId: string; key: string }> {
    const client = this.getClient(cfg);
    const command = new CreateMultipartUploadCommand({
      Bucket: cfg.bucket,
      Key: key,
      ContentType: contentType,
      ServerSideEncryption: 'AES256',
      ACL: 'private',
    });

    const response = await client.send(command);
    if (!response.UploadId) {
      throw new Error('S3 CreateMultipartUpload returned no UploadId');
    }

    return { uploadId: response.UploadId, key };
  }

  static async signPart(
    cfg: DashboardUploaderS3Cfg,
    key: string,
    uploadId: string,
    partNumber: number
  ): Promise<{ url: string; headers?: Record<string, string> }> {
    const client = this.getClient(cfg);
    const command = new UploadPartCommand({
      Bucket: cfg.bucket,
      Key: key,
      UploadId: uploadId,
      PartNumber: partNumber,
    });

    const url = await getSignedUrl(client, command, {
      expiresIn: 3600,
      // Prevent SDK from adding checksum headers that may cause 403 errors
      unhoistableHeaders: new Set(['x-amz-checksum-crc32']),
    });

    return { url };
  }

  static async listParts(
    cfg: DashboardUploaderS3Cfg,
    key: string,
    uploadId: string
  ): Promise<Array<{ PartNumber: number; Size: number; ETag: string }>> {
    const client = this.getClient(cfg);
    const allParts: Array<{ PartNumber: number; Size: number; ETag: string }> = [];
    let partNumberMarker: string | undefined;

    // ListParts is paginated (max 1000 per response)
    do {
      const command = new ListPartsCommand({
        Bucket: cfg.bucket,
        Key: key,
        UploadId: uploadId,
        PartNumberMarker: partNumberMarker,
      });

      const response = await client.send(command);
      if (response.Parts) {
        for (const part of response.Parts) {
          if (part.PartNumber != null && part.Size != null && part.ETag) {
            allParts.push({
              PartNumber: part.PartNumber,
              Size: part.Size,
              ETag: part.ETag,
            });
          }
        }
      }

      partNumberMarker = response.IsTruncated
        ? String(response.NextPartNumberMarker)
        : undefined;
    } while (partNumberMarker);

    return allParts;
  }

  static async completeMultipartUpload(
    cfg: DashboardUploaderS3Cfg,
    key: string,
    uploadId: string,
    parts: Array<{ ETag: string; PartNumber: number }>
  ): Promise<{ location?: string }> {
    const client = this.getClient(cfg);
    const command = new CompleteMultipartUploadCommand({
      Bucket: cfg.bucket,
      Key: key,
      UploadId: uploadId,
      MultipartUpload: {
        Parts: parts.map((p) => ({
          ETag: p.ETag,
          PartNumber: p.PartNumber,
        })),
      },
    });

    const response = await client.send(command);
    return { location: response.Location };
  }

  static async abortMultipartUpload(
    cfg: DashboardUploaderS3Cfg | null,
    key: string,
    uploadId: string
  ): Promise<void> {
    // Credentials cleared during customer switch — skip cleanup.
    // S3 bucket lifecycle rules auto-expire incomplete multipart uploads.
    if (!cfg) return;

    const client = this.getClient(cfg);
    const command = new AbortMultipartUploadCommand({
      Bucket: cfg.bucket,
      Key: key,
      UploadId: uploadId,
    });

    await client.send(command);
  }
}
